import { Component, OnDestroy, OnInit } from '@angular/core';
import { DictionaryService, IosDialogService, LicensesService, ProfileService } from '@app/services';
import { Utils } from '@app/shared';
import { FilterFields, Filters, License, LicenseBundleResponse, LicenseBundleToUpdate, LicenseResponse, SingleFilter, UserType } from '@app/models';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { CustomDataSource, Sort } from '@app/classes';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, Subscription } from 'rxjs';
import { ActivateLicenseDialogComponent } from './activate-license-dialog/activate-license-dialog.component';
import { ActivationSuccessDialogComponent } from './activation-success-dialog/activation-success-dialog.component';

@Component({
  selector: 'app-licenses',
  templateUrl: './licenses.component.html',
  styleUrls: ['./licenses.component.scss'],
})
export class LicensesComponent implements OnInit, OnDestroy {
  loading: boolean = true;
  search: FormControl<string> = new FormControl<string>(null);
  expiredOnly: number = 0;
  sort: Sort[] = [];
  options: { label: string; value: number }[] = [
    { label: 'LABELS.ACTIVE', value: 0 },
    { label: 'LABELS.EXPIRED', value: 1 },
  ];

  update$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  dataSource: CustomDataSource<License> = new CustomDataSource<License>(this.update$, 20);
  subscriptions: Subscription[] = [];

  constructor(
    private profileService: ProfileService,
    private translate: TranslateService,
    private licenseService: LicensesService,
    private dialog: IosDialogService,
  ) {}

  async ngOnInit() {

    await this.getLicenses();
    const fetchSub = this.update$.subscribe((res: number) => {
      if (res > 0) this.getLicenses(res);
    });
    this.subscriptions.push(fetchSub);
    const searchSub = this.search.valueChanges
      .pipe(
        filter((res: string) => res.length >= 3 || res === ''),
        debounceTime(250),
        distinctUntilChanged(),
      )
      .subscribe((term) => {
        if (term == null) return;
        this.dataSource.clearCache();
        this.getLicenses();
      });
    this.subscriptions.push(searchSub);

    this.licenseService.updateLicenseBundleById$.subscribe((licenseBundleToUpdate: LicenseBundleToUpdate) => {
      if (!licenseBundleToUpdate.licenseBundleId) return;
      // Cerco tutte le licenze con lo stesso bundleId e le aggiorno
      this.updateLicenseBundle(licenseBundleToUpdate);
    });

    this.licenseService.reloadLicenses$.subscribe(() => this.getLicenses());
  }

  ngOnDestroy() {
    this.subscriptions = Utils.unsubscribe(this.subscriptions);
  }

  changeSort(event: Sort[]) {
    this.sort = event;
    this.dataSource.clearCache();
    this.getLicenses();
  }

  changeLicenseState() {
    this.dataSource.clearCache();
    this.getLicenses();
  }

  async getLicenses(page: number = 0, options?: { loadAll?: boolean }) {
    const filters = new Filters({
      limit: 20,
      page: page + 1,
      selectFields: this.buildSelectedFields(),
      filterFields: this.buildFilterFields(),
      singleFilter: this.buildSingleFilters(),
      sort: this.buildSort(),
    });

    const data = await this.licenseService.list(filters);
    this.dataSource.totalCount = data.totalCount;
    this.dataSource.addPageCache(data.result, page);
    this.dataSource.hasMore = data.hasMore;

    if (options?.loadAll && this.dataSource.hasMore) {
      await this.getLicenses(page + 1, options);
    }

    this.loading = false;
  }

  buildSelectedFields = (): string[] => {
    const selectFields = [
      'licenseId',
      'module.name',
      'module.moduleBrandNames.name',
      'brand.name',
      'module.moduleType.code',
      'licenseType.code',
      'licenseKey',
      'licenseDurationTime',
      'licenseDurationUnits',
      'creationTime',
      'creationTime',
      'activationTime',
      'expirationTime',
      'offlineActivation',
      'activationEnabledFrom',
      'activationDisabled',
      'maxWorkstations',
      'hwSerialNumber',
      'usedUnits',
      'usedUnitsLastUpdateTime',
      'licenseBundle.licenseBundleId',
      'licenseBundle.activationTime',
      'licenseBundle.licenseKey',
      'appMode.code',
    ];
    if (this.profileService.userTypeCode === 'CEF' || this.profileService.userTypeCode === 'DEA') {
      selectFields.push('module.code', 'customer.organization', 'customer.fistName', 'customer.lastName', 'customerChangements');
    }
    if (this.profileService.userTypeCode === 'CEF') {
      selectFields.push('orderNumber');
    }
    if (this.profileService.userTypeCode === 'CUS') {
      selectFields.push('offlineLicenseKey', 'licenseBundle.offlineLicenseKey');
      selectFields.push('offlineLicenseKeyExpirationTime');
      selectFields.push('licenseBundle.offlineLicenseKeyExpirationTime');
    }
    if (this.profileService.userTypeCode === 'CEF' || this.profileService.userTypeCode === 'CUS') {
      selectFields.push('dealer.businessName');
    }
    return selectFields;
  };

  buildSingleFilters = (): SingleFilter | null => {
    if (!this.search.value) return null;

    const value = this.search.value;
    if (this.profileService.userTypeCode === 'CUS') {
      return new SingleFilter({ fields: ['module.code', 'module.name'], partialMatch: true, value });
    }
    if (this.profileService.userTypeCode === 'CEF') {
      return new SingleFilter({
        fields: ['module.code', 'module.name', 'orderNumber', 'customer.organization', 'customer.firstName', 'customer.lastName'],
        partialMatch: true,
        value,
      });
    }
    if (this.profileService.userTypeCode === 'DEA') {
      return new SingleFilter({
        fields: ['module.code', 'module.name', 'customer.organization', 'customer.firstName', 'customer.lastName'],
        partialMatch: true,
        value,
      });
    }

    return null;
  };

  buildFilterFields = (): FilterFields[] => {
    return [new FilterFields({ fieldName: 'expired', value: this.expiredOnly.toString(), operator: 'EQUAL' })];
  };

  buildSort = (): [string, 'ASC' | 'DESC' | undefined][] => {
    return this.sort.filter((x) => x.active == 1).map((x) => [x.property, x.direction]);
  };

  openActivationDialog() {
    this.dialog
      .openAndWaitResponse<{ licenses: LicenseResponse[]; licenseBundle?: LicenseBundleResponse }>(ActivateLicenseDialogComponent, {
        header: this.translate.instant('BUTTONS.ACTIVATE_OFFLINE_LICENSE'),
        width: 'min(450px, 90vw)',
      })
      .then((res) => {
        if (!res) return;

        this.dialog
          .openAndWaitResponse(ActivationSuccessDialogComponent, {
            header: this.translate.instant('LABELS.ACTIVATION_SUCCESFUL'),
            width: 'min(600px, 90vw)',
            height: res.licenses.length === 1 ? 'min(400px, 90vh)' : 'min(650px, 90vh)',
            data: {
              licenses: res.licenses,
              licenseBundle: res.licenseBundle,
            },
          })
          .then(() => this.getLicenses(0, { loadAll: true }));
      });
  }

  private updateLicenseBundle(licenseBundleToUpdate: LicenseBundleToUpdate) {
    for (const license of this.dataSource.allData) {
      if (
        license.licenseBundle?.licenseBundleId == licenseBundleToUpdate.licenseBundleId &&
        license.activationTime != null &&
        license.offlineActivation &&
        !license.offlineLicenseKey
      ) {
        license.licenseBundle.offlineLicenseKey = licenseBundleToUpdate.offlineLicenseKey;
        license.licenseBundle.offlineLicenseKeyExpirationTime = licenseBundleToUpdate.offlineLicenseKeyExpirationTime;
      }
    }
    this.getLicenses();
  }
}
